import { useMemo, useState, type ReactElement } from "react";
import {
  MAX_EXPOSURE_RISKS,
  type ExposureRiskCandidate,
  type ExposureRiskReviewParams,
} from "@playbookai/widget-schemas";
import type { WidgetBodyProps } from "../types.js";
import { RiskSummary } from "./Component.js";
import { CandidateRiskEditor } from "./CandidateRiskEditor.js";
import { RiskList, type RiskFilters } from "./RiskList.js";
import { sortExposureRisks } from "./presentation.js";

const NO_FILTERS: RiskFilters = {
  search: "",
  type: "all",
  category: "all",
  criticality: "all",
};

export function ExposureRiskReviewExpandedEditor({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<ExposureRiskReviewParams>): ReactElement {
  const [filters, setFilters] = useState<RiskFilters>(NO_FILTERS);
  const [requestedId, setRequestedId] = useState<string | undefined>(undefined);

  const visibleRisks = useMemo(
    () => sortExposureRisks(data.risks.filter(matches(filters))),
    [data.risks, filters],
  );
  // Selection is presentation state: it survives edits, and falls back to the
  // top of the current view when the requested risk is deleted or filtered out.
  const selected =
    visibleRisks.find((risk) => risk.id === requestedId) ?? visibleRisks[0];

  const updateRisk = (
    riskId: string,
    patch: Partial<ExposureRiskCandidate>,
  ): void => {
    if (readOnly) return;
    onChange({
      ...data,
      risks: data.risks.map((risk) =>
        risk.id === riskId ? { ...risk, ...patch } : risk,
      ),
    });
  };

  const addRisk = (): void => {
    if (readOnly || data.risks.length >= MAX_EXPOSURE_RISKS) return;
    const id = nextRiskId();
    setRequestedId(id);
    setFilters(NO_FILTERS);
    onChange({
      ...data,
      risks: [
        ...data.risks,
        {
          id,
          title: "New candidate risk",
          type: "exposure",
          category: "operation",
          description: "",
          affectedScope: "",
          basis: "",
          criticality: "unknown",
          assumptions: [],
        },
      ],
    });
  };

  const deleteRisk = (riskId: string): void => {
    if (readOnly) return;
    setRequestedId(undefined);
    onChange({
      ...data,
      risks: data.risks.filter((risk) => risk.id !== riskId),
    });
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-4 overflow-y-auto">
      <RiskSummary data={data} />

      <div className="grid min-h-0 min-w-0 flex-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <RiskList
          risks={visibleRisks}
          total={data.risks.length}
          filters={filters}
          selectedId={selected?.id}
          readOnly={readOnly === true}
          canAdd={data.risks.length < MAX_EXPOSURE_RISKS}
          onFiltersChange={setFilters}
          onSelect={setRequestedId}
          onAdd={addRisk}
        />
        {selected ? (
          <CandidateRiskEditor
            key={selected.id}
            risk={selected}
            readOnly={readOnly}
            onChange={(patch) => updateRisk(selected.id, patch)}
            onDelete={() => deleteRisk(selected.id)}
          />
        ) : (
          <div className="grid min-h-48 place-items-center rounded-lg border border-dashed border-pb-border px-4 text-center text-sm text-pb-text-muted">
            {data.risks.length === 0
              ? "Add a candidate risk to start the review."
              : "Select a candidate risk to review its scope, basis and assumptions."}
          </div>
        )}
      </div>
    </div>
  );
}

function matches(
  filters: RiskFilters,
): (risk: ExposureRiskCandidate) => boolean {
  const query = filters.search.trim().toLowerCase();
  return (risk) => {
    if (filters.type !== "all" && risk.type !== filters.type) return false;
    if (filters.category !== "all" && risk.category !== filters.category) {
      return false;
    }
    if (
      filters.criticality !== "all" &&
      risk.criticality !== filters.criticality
    ) {
      return false;
    }
    if (query === "") return true;
    return [
      risk.title,
      risk.description,
      risk.affectedScope,
      risk.basis,
      ...risk.assumptions,
    ].some((field) => field.toLowerCase().includes(query));
  };
}

function nextRiskId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `risk-${crypto.randomUUID()}`;
  }
  return `risk-${Date.now().toString(36)}`;
}
